import type {
  PaymentMethodOption,
  PaymentMethodSelectorProps,
} from '@engyalo/delivery-ui-components'
import { CheckoutRules } from '../interfaces'
import type { ICheckoutRuleValue } from '../graphQL/queries/getCheckoutRule/types'
import type { RootState } from '../redux/store'
import { isHidePricesEnabled } from './hidePrices'

/**
 * The panel props this adapter owns, checked against the library's own prop
 * shape.
 *
 * The same guard the three shipped panels use, catching the same drift: the
 * injector is registered in a `Map` whose value type is `UnresolvedRecord`, so
 * a library rename of `selectedMethodId` would compile, land unread on the
 * picker, and silently stop highlighting the shopper's choice.
 *
 * **It guards names and value types, not presence.** Every prop here is
 * optional, so `{}` satisfies this type — dropping a line from the returned
 * object is a compile *success*. The colocated assertions cover that instead,
 * which is why each forwarded prop has a test naming it.
 *
 * `onMethodSelect` is deliberately absent, and it is the picker's one
 * **required** prop. It is an authored action, not derived state: config binds
 * it to `templateActions.paymentMethod.setPaymentMethod` through
 * `cinnamonActions`, and `processActions` turns that into the callable the
 * library receives. Injecting a function here would take the storefront's
 * ability to author it away for no gain.
 */
type InjectedPaymentMethodProps = Pick<
  PaymentMethodSelectorProps,
  'methods' | 'selectedMethodId' | 'hidePrices'
>

/**
 * The wire's options, shaped into the rows the picker draws.
 *
 * **The wire carries no id.** `getCheckoutRule` returns `{ data, amount }`
 * entries where `data` is the backend enum value — `CASH`, `CREDIT`,
 * `TRANSFER`, `YALO_PAGO` — and the picker wants `{ id, method }`. So `id` and
 * `method` are both that value. They are the same string for a reason: `id` is
 * what `onMethodSelect` hands back, and the write needs the backend value, so
 * any other id would have to be mapped back on the way out.
 *
 * `amount` is dropped, and every entry carries `0` today — both resolvers
 * hard-code it on the non-external-API path. When a storefront starts charging
 * a surcharge it belongs in `tag` or `helperText`, which are authored per
 * option and go through the `hidePrices` gate below.
 *
 * An entry with no usable `data` is dropped rather than mapped. The array
 * arrives from untyped JSON by way of a checkout rule, so a `null` in it is
 * reachable and `null.data` throws — a blank screen over a working storefront.
 *
 * ⚠️ **The picker's row count can still be lower than this one, and cinnamon
 * cannot predict by how much.** `buildMethodRows` drops duplicate ids and any
 * row whose resolved label is empty — and a label resolves through
 * `paymentMethodLabels`, which is authored config this adapter never sees.
 * Mapping a method to `''` there withdraws it from the picker deliberately. So
 * a storefront can suppress every option and leave
 * `checkout.ui.PAYMENT_METHODS.emptyHidden` reading `true` over an empty
 * screen. Exact agreement is impossible by construction, not merely unbuilt;
 * do not reimplement the library's drop rules here chasing it.
 */
export const toPaymentMethodOptions = (
  values?: ICheckoutRuleValue[] | null,
): PaymentMethodOption[] | undefined => {
  if (!Array.isArray(values)) return undefined

  return values
    .filter((value): value is ICheckoutRuleValue => !!value?.data)
    .map((value) => ({ id: value.data, method: value.data }))
}

/**
 * The three `PaymentMethodSelector` props cinnamon owns rather than the config
 * author.
 *
 * Injected in `useCinnamonProps` for any component whose `importRoute` is
 * `PaymentMethodSelector`, exactly as `getCartCheckoutPanelProps` is for the
 * cart. None is authorable as a `{{variable}}`: two are shapes the config
 * language cannot build, and one is a predicate it cannot compute.
 *
 * Everything else stays in config — `paymentLabel`, `paymentMethodLabels`, the
 * per-option `tag` and `helperText`, every colour prop, `fieldsToDisplay`, the
 * two custom slots, and `onMethodSelect`. This file adds no layout or copy
 * decision.
 *
 * **`methods` is `undefined` before a fetch, not `[]`.** Both draw nothing —
 * the picker returns `null` with no rows — but the distinction is what keeps
 * the empty-state message off a loading screen, and it is `emptyHidden` on the
 * slice that carries it to config.
 */
export const getPaymentMethodSelectorProps = (
  state: RootState,
): InjectedPaymentMethodProps => ({
  methods: toPaymentMethodOptions(
    state.checkoutSlice.available[CheckoutRules.PAYMENT_METHODS]?.data,
  ),
  /**
   * The shopper's pick, as the picker's `id` — which is the backend value
   * `toPaymentMethodOptions` used for both fields.
   *
   * An id outside `methods` selects nothing, which the library calls the
   * honest rendering of a session carrying a method the storefront no longer
   * offers. That is reachable here: the selection survives a refetch that
   * withdrew it.
   */
  selectedMethodId:
    state.checkoutSlice.selections[CheckoutRules.PAYMENT_METHODS]?.data,
  /**
   * The picker renders no money of its own — a payment *method* is not an
   * amount — so this is entirely for the tenant strings. `tag`, `helperText`
   * and the two slots all take free text, and `maskPricesForDisplay` is an
   * allowlist of prop *names* over *numbers*, so it cannot see the price
   * inside `'CASH — $50 de recargo'`.
   */
  hidePrices: isHidePricesEnabled(state),
})

export default getPaymentMethodSelectorProps
